import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Zap, ChevronRight, Sparkles, ShieldCheck } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useCopyScoring } from '../hooks/useCopyScoring';
import { SWIPE_FILE_EXERCISES } from '../data/copywritingExercises';
import CopyEditor from '../components/CopyEditor';
import CopyScoreDisplay from '../components/CopyScoreDisplay';

const ACCENT = '#7c3aed';

const SwipeFile = () => {
  usePageTitle('The Swipe File | Castle Atlas');
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { scoreExercise, loading, error, clearError } = useCopyScoring();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  const [currentIdx, setCurrentIdx] = useState(0);
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (exerciseId) {
      const idx = SWIPE_FILE_EXERCISES.findIndex(e => e.id === exerciseId);
      if (idx >= 0) setCurrentIdx(idx);
    }
  }, [exerciseId]);

  const exercise = SWIPE_FILE_EXERCISES[currentIdx];
  const isRevision = exercise?.mode === 'revision';

  const handleSubmit = useCallback(async () => {
    if (submitted || !headline.trim()) return;
    setSubmitted(true);

    const userCopy = `SUBJECT: ${headline}\n\nBODY: ${body}`;

    try {
      const res = await scoreExercise({
        exerciseId: exercise.id,
        moduleId: 'swipe-file',
        mode: exercise.mode,
        userText: userCopy,
      });
      setResult(res);
      setScore(res.scores?.overall ?? 0);
    } catch (err) {
      // unlock so the student can retry; the hook holds the message shown under the button
      console.error(err);
      setSubmitted(false);
    }
  }, [submitted, headline, body, exercise, scoreExercise]);

  const nextExercise = () => {
    if (currentIdx < SWIPE_FILE_EXERCISES.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setHeadline(''); setBody('');
      setSubmitted(false); setResult(null); setScore(null); clearError();
      navigate(`/skills/copywriting/swipe-file/${SWIPE_FILE_EXERCISES[currentIdx + 1].id}`, { replace: true });
    }
  };

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    originalBg: isDark ? 'linear-gradient(145deg,#100b1e 0%,#160e28 60%,#1c1232 100%)' : 'linear-gradient(145deg,#faf5ff 0%,#ede9fe 60%,#ddd6fe 100%)',
    originalBorder: isDark ? '#4c3580' : '#c4b5fd',
    originalText: isDark ? '#c4b5fd' : '#4c1d95',
    draftBg: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
    draftBorder: isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.3)',
  };

  const AxisPill = ({ label, muted }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      background: muted ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'rgba(124,58,237,0.10)',
      color: muted ? c.muted : ACCENT, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{label}</span>
  );

  if (!exercise) return null;

  return (
    <div style={{ maxWidth: '100%', paddingBottom: 80, background: c.pageBg, minHeight: '100vh' }}>
    <div className="rail-clearance">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);} }
        .anim-fade { animation: fadeUp 0.3s ease-out both; }
      `}</style>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: isDark ? 'rgba(14,13,12,0.85)' : 'rgba(250,249,247,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/skills/copywriting')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8,
        }}>
          <ArrowLeft size={18} color={c.head} />
        </button>
        <FileText size={16} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 800, color: c.head, flex: 1 }}>The Swipe File</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>
          {currentIdx + 1} / {SWIPE_FILE_EXERCISES.length}
        </span>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '16px 16px 40px' : '24px 24px 60px' }}>

        {/* Original — shown up front, this is the "swipe" the student studies before writing */}
        {exercise.original && (
          <div className="anim-fade" style={{
            background: c.originalBg, border: `1.5px solid ${c.originalBorder}`,
            borderRadius: 20, padding: '18px 20px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Sparkles size={13} color={ACCENT} />
              <span style={{ fontSize: 10, fontWeight: 700, color: c.originalText, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                The original
              </span>
            </div>
            <div style={{
              fontSize: 13, color: c.originalText, lineHeight: 1.7,
              whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', marginBottom: 14,
            }}>
              {exercise.original}
            </div>
            {exercise.provenance && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 6,
                paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(196,181,253,0.15)' : 'rgba(124,58,237,0.15)'}`,
              }}>
                <ShieldCheck size={13} color={c.originalText} style={{ marginTop: 1, flexShrink: 0 }} />
                <div style={{ fontSize: 11, color: c.originalText, opacity: 0.85, lineHeight: 1.5 }}>
                  {exercise.provenance}{exercise.paraphraseFlag ? ` ${exercise.paraphraseFlag}` : ''}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Brief Card — the adaptation task */}
        <div className="anim-fade" style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 20, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <AxisPill label={exercise.primaryAxis} />
            {exercise.secondaryAxes?.map(a => <AxisPill key={a} label={a} muted />)}
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: c.head, marginBottom: 10 }}>{exercise.title}</h2>
          <p style={{
            fontSize: 13, color: c.sub, lineHeight: 1.7, whiteSpace: 'pre-wrap',
            ...(isRevision ? { background: c.draftBg, border: `1px solid ${c.draftBorder}`, borderRadius: 12, padding: '12px 14px' } : {}),
          }}>
            {exercise.adaptTask}
          </p>
        </div>

        {/* Editor */}
        <div className="anim-fade" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            {isRevision ? 'Your fixed version' : 'Your adaptation'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <CopyEditor
              key={`${exercise.id}:subject`}
              draftKey={`${exercise.id}:subject`}
              value={headline}
              onChange={setHeadline}
              placeholder="Subject line..."
              label="Subject"
              maxLength={100}
              rows={2}
              isDark={isDark}
              autoFocus
              disabled={submitted}
            />
            <CopyEditor
              key={`${exercise.id}:body`}
              draftKey={`${exercise.id}:body`}
              value={body}
              onChange={setBody}
              placeholder="Body..."
              label="Body"
              rows={6}
              isDark={isDark}
              disabled={submitted}
            />
          </div>

          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={!headline.trim() || loading}
              style={{
                width: '100%', marginTop: 16, padding: '14px 24px', borderRadius: 12,
                background: (!headline.trim() || loading) ? (isDark ? '#2d2825' : '#e8e4df') : ACCENT,
                color: (!headline.trim() || loading) ? c.muted : '#fff',
                fontSize: 14, fontWeight: 700, border: 'none', cursor: (!headline.trim() || loading) ? 'not-allowed' : 'pointer',
                boxShadow: (!headline.trim() || loading) ? 'none' : `0 4px 20px -4px rgba(124,58,237,0.45)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Zap size={15} />
              {loading ? 'Scoring...' : 'Submit Adaptation'}
            </button>
          )}
          {error && !submitted && (
            <p role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#ef4444', lineHeight: 1.5 }}>
              {error} Your answer is still here; try again.
            </p>
          )}
        </div>

        {/* Results */}
        {submitted && result && (
          <div className="anim-fade">
            <CopyScoreDisplay
              score={score}
              isNew
              isDark={isDark}
              extraScores={result.scores}
              feedback={result.feedback}
            />

            {currentIdx < SWIPE_FILE_EXERCISES.length - 1 && (
              <button
                onClick={nextExercise}
                style={{
                  width: '100%', marginTop: 20, padding: '14px 24px', borderRadius: 12,
                  background: ACCENT, color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: `0 4px 20px -4px rgba(124,58,237,0.45)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Next Exercise <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default SwipeFile;
